/**
 * PATH: packages/tenant-shared/src/lib/utils.ts
 * FILE_ID: TENANTSHARED-UTILS-001
 * PURPOSE: Tenant-agnostic UI/format utilities used by every Quotech
 *          tenant SPA (currently newmark + sam, future tenants too).
 * WHY:     Until MR #7 these helpers existed as a byte-identical pair
 *          at apps/newmark/src/shared/lib/utils.ts and
 *          apps/sam/src/shared/lib/utils.ts. Two clones means a fix
 *          to `formatCurrency` only landed for one tenant. Lifted
 *          here so a single source of truth feeds both tenant shims.
 * FLOW:
 *   ┌─────────────┐   ┌──────────────────┐   ┌─────────────────┐
 *   │ raw value   │ → │ Intl/clsx/twMerge │ → │ formatted output │
 *   └─────────────┘   └──────────────────┘   └─────────────────┘
 * DEPENDENCIES:
 *   - clsx + tailwind-merge — class-name merging for tailwind utility
 *     conflicts (the `cn(...)` helper)
 * RELATED:
 *   - apps/{newmark,sam}/src/shared/lib/utils.ts — thin re-export shims
 */

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatCurrency(value: number | string, compact = false): string {
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (isNaN(num)) return '$0'
  if (compact) {
    if (Math.abs(num) >= 1_000_000) return `$${(num / 1_000_000).toFixed(1)}M`
    if (Math.abs(num) >= 1_000) return `$${(num / 1_000).toFixed(0)}K`
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num)
}

export function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`
}

export function formatAuDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' })
  } catch {
    return '—'
  }
}
